import { useRef, useState } from "react"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import cx from "clsx"
import PlusIcon from "@rawa/ui/assets/icon-plus.svg?react"
import LayersIcon from "../../../assets/icon-layers.svg?react"
import SearchIcon from "../../../assets/icon-search.svg?react"
import type { FormModule } from "shared"
import { archiveFormModule, deleteFormModule, fetchFormModules } from "../api"
import { ConfirmDialog } from "../components/ConfirmDialog"
import { AdminButton, AdminButtonLink } from "@rawa/ui/AdminButton"
import { pathHelpers, paths } from "../../../paths"
import { useDebounce } from "../../../hooks/useDebounce"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import { isForbidden } from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./ModulesListPage.module.css"

// The same pause the new-module form waits before checking a name, so the two
// admin screens feel alike under typing.
const SEARCH_DEBOUNCE_MS = 400

export function ModulesListPage() {
  const [search, setSearch] = useState("")
  const settledSearch = useDebounce(search, SEARCH_DEBOUNCE_MS)
  const queryClient = useQueryClient()
  // What was last taken off the list, and how, for a screen reader to hear.
  const [withdrawn, setWithdrawn] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<FormModule | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const {
    data: formModules = [],
    isPending,
    isError,
    error,
    isPlaceholderData,
  } = useQuery({
    // "list", so no search term can be read as another query under
    // ["form-modules"], such as the template library's "published".
    queryKey: ["form-modules", "list", settledSearch],
    queryFn: ({ signal }) => fetchFormModules(settledSearch, signal),
    // The list a term narrows stays on screen while the next one is fetched,
    // rather than the screen falling back to its skeleton on every keystroke.
    placeholderData: keepPreviousData,
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchFormModules", forbiddenIsExpected: true },
  })

  // Spoken, not shown: the list changing under a search or an archive is
  // silent to a screen reader otherwise.
  const searchResult =
    isPending || isPlaceholderData || isError || settledSearch === ""
      ? ""
      : formModules.length === 0
        ? "No matches."
        : `${formModules.length} ${formModules.length === 1 ? "module" : "modules"}`
  const announcement = withdrawn ?? searchResult

  // A 403 is an authorization outcome with a screen of its own; anything else
  // is a failure to load.
  const forbidden = isError && isForbidden(error)
  const loadError = isError && !forbidden

  // Deleted or archived as withdrawalOf says. Re-reading the list is what
  // takes the row away, so a search that was in flight when the change
  // landed cannot put it back.
  const withdraw = useMutation({
    mutationFn: (formModule: FormModule) => withdrawalOf(formModule).run(),
    onError: (err, formModule) =>
      trackError(err, { source: withdrawalOf(formModule).source }),
    onSuccess: (_result, formModule) => {
      setWithdrawn(`${withdrawalOf(formModule).done} ${formModule.name}.`)
      // The row it was on is going, so focus needs somewhere deliberate
      // to land. The search box is on screen whether or not any rows are.
      searchRef.current?.focus()
      return queryClient.invalidateQueries({ queryKey: ["form-modules"] })
    },
  })

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>Form administration</p>
          <h1 className={cx(typography.h2, styles.heading)}>Modules</h1>
          <p className={styles.subtitle}>
            Reusable groups of fields. Build one once, then use it on any form.
          </p>
        </div>
        {/* A link, so it cannot be disabled while loading the way the other
            admin pages disable their action button — it waits instead. */}
        {!isPending && !forbidden && (
          <AdminButtonLink href={paths.newModule} className={styles.addButton}>
            <PlusIcon className={styles.addIcon} aria-hidden="true" />
            Create module
          </AdminButtonLink>
        )}
      </div>

      {forbidden && <ErrorCallout>{NOT_AUTHORIZED_MESSAGE}</ErrorCallout>}

      {isPending && !isError && (
        <div
          role="status"
          aria-label="Loading modules"
          className={styles.skeletonPanel}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.skeletonRow}>
              <span className={styles.skeletonName} />
            </div>
          ))}
        </div>
      )}

      {/* Left up through a failed load: changing the term is what asks
          again, so taking it away would strand the screen. */}
      {!isPending && !forbidden && (
        <label className={styles.searchField}>
          <SearchIcon className={styles.searchIcon} aria-hidden="true" />
          <input
            type="search"
            className={styles.search}
            aria-label="Search modules"
            placeholder="Search modules by name or description"
            ref={searchRef}
            value={search}
            onChange={(e) => {
              setWithdrawn(null)
              setSearch(e.target.value)
            }}
          />
        </label>
      )}

      {loadError && <ErrorCallout>Failed to load modules.</ErrorCallout>}

      {withdraw.isError && withdraw.variables && (
        <ErrorCallout>
          Could not {withdrawalOf(withdraw.variables).verb}{" "}
          {withdraw.variables.name}. Please try again.
        </ErrorCallout>
      )}

      {!isPending && !loadError && !forbidden && formModules.length === 0 && (
        <p className={styles.empty}>
          {settledSearch === "" ? "No modules yet." : "No matches."}
        </p>
      )}

      {!isPending && !loadError && formModules.length > 0 && (
        <ul
          aria-label="Modules"
          className={styles.list}
          aria-busy={isPlaceholderData}
        >
          {formModules.map((formModule) => (
            <li key={formModule.id} className={styles.card}>
              <span className={styles.glyph} aria-hidden="true">
                <LayersIcon />
              </span>
              <div className={styles.body}>
                <span className={styles.name}>{formModule.name}</span>
                {formModule.description && (
                  <p className={styles.description}>{formModule.description}</p>
                )}
                <span className={styles.chip}>
                  {formModule.entityLinkLabel}
                </span>
                <StatusTags formModule={formModule} />
              </div>
              <AdminButtonLink
                variant="outline"
                href={pathHelpers.toModule(formModule.id)}
                className={styles.rowAction}
                ariaLabel={`Edit ${formModule.name}`}
              >
                Edit
              </AdminButtonLink>
              <WithdrawButton
                formModule={formModule}
                pending={
                  withdraw.isPending && withdraw.variables.id === formModule.id
                }
                onClick={() =>
                  withdrawalOf(formModule).asks
                    ? setDeleting(formModule)
                    : withdraw.mutate(formModule)
                }
              />
            </li>
          ))}
        </ul>
      )}

      <span role="status" aria-live="polite" className={styles.srOnly}>
        {announcement}
      </span>
      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.name ?? ""}?`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) withdraw.mutate(deleting)
          setDeleting(null)
        }}
      >
        It has never been published, so nothing uses it. Deleting it frees its
        name, and cannot be undone.
      </ConfirmDialog>
    </div>
  )
}

/**
 * How a module comes off the list (ADR-040): one never published is deleted,
 * freeing its name, once confirmed — it cannot be undone; one that has
 * published is archived, since notes and templates hold its versions.
 */
function withdrawalOf(formModule: FormModule) {
  return formModule.publishedVersion === null
    ? {
        verb: "delete",
        label: "Delete",
        pending: "Deleting…",
        done: "Deleted",
        source: "deleteFormModule",
        asks: true,
        run: () => deleteFormModule(formModule.id),
      }
    : {
        verb: "archive",
        label: "Archive",
        pending: "Archiving…",
        done: "Archived",
        source: "archiveFormModule",
        asks: false,
        run: () => archiveFormModule(formModule.id),
      }
}

/** Whether templates can use the module, and at which version, and whether
 * it holds saved changes not yet published. */
function StatusTags({ formModule }: { formModule: FormModule }) {
  if (formModule.publishedVersion === null) {
    return <span className={styles.chip}>Not published</span>
  }
  return (
    <>
      <span className={styles.publishedChip}>
        Published v{formModule.publishedVersion}
      </span>
      {formModule.unpublishedChange !== null && (
        <span className={styles.chip}>Draft</span>
      )}
    </>
  )
}

/** Takes a module off the list, as withdrawalOf says. */
function WithdrawButton({
  formModule,
  pending,
  onClick,
}: {
  formModule: FormModule
  pending: boolean
  onClick: () => void
}) {
  const withdrawal = withdrawalOf(formModule)
  return (
    <AdminButton
      variant="outline"
      className={styles.rowAction}
      onClick={onClick}
      disabled={pending}
      ariaLabel={`${withdrawal.label} ${formModule.name}`}
    >
      {pending ? withdrawal.pending : withdrawal.label}
    </AdminButton>
  )
}
